<template>
  <div class="youngs-content">
    <section class="youngs-chat">
      <div class="body-content-overlay" :class="{ 'show': shallShowYoungDetailsSidebar }"
           @click="shallShowYoungDetailsSidebar = false" />
      <div class="body-content-overlay" :class="{ 'show': shallShowYoungCommentsSidebar }"
           @click="shallShowYoungCommentsSidebar = false" />

      <div v-if="!activeYoung || !activeYoung.id" class="start-chat-area">
        <div class="start-chat-icon" :class="{ 'cursor-pointer': isWindowSmall }" @click="openLeftSidebar">
          <feather-icon icon="UserIcon" size="56" />
        </div>
      </div>
      <div v-else class="active-chat">
        <!-- Navbar -->
        <div class="chat-navbar">
          <header class="chat-header">
            <div class="d-flex align-items-center">
              <div class="sidebar-toggle d-block d-lg-none mr-1">
                <feather-icon icon="MenuIcon" class="cursor-pointer" size="21" @click="mqShallShowLeftSidebar = true" />
              </div>
              <div class="cursor-pointer d-flex align-items-center" @click="shallShowYoungDetailsSidebar = true">
                <h6 class="mb-0 ml-50 mr-1">
                  {{ activeYoung.lastName }} {{ activeYoung.firstName }}
                </h6>
                <p class="mb-0 mr-1">
                  {{ formatPhoneNumber(activeYoung.phoneNumber) }}
                </p>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <b-button
                v-if="canOpenConversation"
                variant="success"
                class="icon-container mr-1"
                :disabled="isOpeningConversation"
                @click="openConversationHandler"
              >
                <b-spinner v-if="isOpeningConversation" small />
                <template v-else>
                  <p>Prendre en charge</p>
                </template>
              </b-button>
              <b-button
                v-if="hasSmsPermission && isHandler && isConversationInProgress"
                v-b-tooltip.hover.bottom="'Remet en file d\'attente sans clôturer'"
                variant="outline-warning"
                class="icon-container mr-1"
                @click="changeConversationStatus(activeYoung.id)"
              >
                <p>Mettre en attente</p>
              </b-button>
              <b-button
                v-if="hasSmsPermission && isHandler && isConversationInProgress"
                v-b-tooltip.hover.bottom="'Archive cette conversation'"
                variant="outline-danger"
                class="icon-container mr-1"
                @click="closeConversationHandler"
              >
                <p>Archiver</p>
                <feather-icon icon="ArchiveIcon" size="17" class="d-sm-block d-none mr-50" />
              </b-button>
              <b-button variant="outline-primary" class="icon-container mr-1"
                        @click="shallShowYoungCommentsSidebar = true;fetchComment(activeYoung.id);">
                <p>Historique</p>
                <feather-icon icon="MessageSquareIcon" size="17" class="d-sm-block d-none mr-50" />
              </b-button>
              <b-button variant="outline-primary" class="icon-container" @click="shallShowYoungDetailsSidebar = true">
                <p>Fiche Jeune</p>
                <feather-icon icon="UserIcon" size="17" class="d-sm-block d-none mr-50" />
              </b-button>
            </div>
          </header>
        </div>
        <!-- Chat -->
        <vue-perfect-scrollbar :settings="perfectScrollbarSettings" class="user-chats scroll-area">
          <young-chat :messages="messages" :is-loading="isChatLoading" :show-rgpd="hasYoungMessages" />
        </vue-perfect-scrollbar>
        <!-- SMS Input -->
        <div v-if="canSendSms" class="chat-app-form">
          <div class="chat-input-wrapper flex-grow-1 position-relative">
            <b-form-textarea
              v-model="messageText"
              placeholder="Écrire un message SMS..."
              rows="1"
              max-rows="4"
              class="chat-textarea"
              :disabled="isSending"
              @keydown.enter.exact.prevent="sendSms"
            />
            <small
              class="char-counter position-absolute d-none d-sm-block text-muted"
            >
              {{ messageText.length }} / 160
            </small>
          </div>
          <b-button
            variant="primary"
            class="btn-send d-flex align-items-center justify-content-center"
            :disabled="!messageText.trim() || isSending"
            @click="sendSms"
          >
            <b-spinner v-if="isSending" small />
            <feather-icon v-else icon="SendIcon" size="16" />
          </b-button>
        </div>
      </div>
    </section>

    <!-- Young Details Sidebar -->
    <young-details :shall-show-young-details-sidebar.sync="shallShowYoungDetailsSidebar" :young="activeYoung"
                   @open-chat="openChat" />
    <young-comments :shall-show-young-comments-sidebar.sync="shallShowYoungCommentsSidebar" :young="activeYoung"
                    :young-comments="youngsComment" @open-chat="openChat" @updateYoungComments="updateYoungComments" />

    <!-- Young List Sidebar -->
    <portal to="content-renderer-sidebar-left">
      <young-list :youngs="youngs" :active-chat-young-id="activeYoung ? activeYoung.id : null"
                  :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
                  :is-loading="isYoungListLoading"
                  :pagination-info="paginationInfo"
                  @open-chat="openChat"
                  @search="handleSearch"
                  @loadMore="handleLoadMore" />
    </portal>
  </div>
</template>

<script>
import store from '@/store' // eslint-disable-line
import { useResponsiveAppLeftSidebarVisibility } from '@core/comp-functions/ui/app' // eslint-disable-line
import { formatPhoneNumber } from '@core/utils/filter' // eslint-disable-line
import {
  computed, ref, onUnmounted, onMounted,
} from '@vue/composition-api'
import {
  BButton,
  BFormTextarea,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { $themeBreakpoints } from '@themeConfig'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// eslint-disable-next-line import/no-cycle
import useYoungsSocket from './useYoungsSocket'
import YoungChat from './YoungChat.vue'
import YoungDetails from './details/YoungDetails.vue'
import YoungComments from './YoungComments.vue'
import YoungList from './YoungList.vue'

export default {
  components: {
    BButton,
    BFormTextarea,
    BSpinner,
    VuePerfectScrollbar,
    YoungChat,
    YoungDetails,
    YoungComments,
    YoungList,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },

  setup(props, { root }) {
    const YOUNGS_STORE_MODULE_NAME = 'youngs'
    const INITIAL_DATA_STORE_MODULE_NAME = 'initialData'

    const { registerSocket, unregisterSocket } = useYoungsSocket(YOUNGS_STORE_MODULE_NAME)
    const youngs = computed(() => store.getters[`${YOUNGS_STORE_MODULE_NAME}/youngs`])
    const paginationInfo = computed(() => store.getters[`${YOUNGS_STORE_MODULE_NAME}/pagination`])
    const searchParams = computed(() => store.getters[`${YOUNGS_STORE_MODULE_NAME}/searchParams`])

    const toast = useToast()

    const displayToast = (title, icon, mode) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          position: 'bottom-right',
          icon,
          variant: mode,
        },
      })
    }

    const isYoungListLoading = ref(false) // the property is passed to younglist

    const { youngId } = root.$route.query

    if (!youngId) {
      isYoungListLoading.value = true
      store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/fetchYoungs`, {
        page: 1,
        pageSize: paginationInfo.value.pageSize,
      }).finally(() => {
        isYoungListLoading.value = false
      })
    }

    registerSocket()

    // Unregister socket connection
    onUnmounted(() => {
      unregisterSocket()
    })

    // UI
    const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility()
    const isWindowSmall = computed(() => store.state.app.windowWidth < $themeBreakpoints.lg)
    const openLeftSidebar = () => {
      if (isWindowSmall.value) {
        mqShallShowLeftSidebar.value = true
      }
    }
    const perfectScrollbarSettings = { maxScrollbarLength: 150 }
    const shallShowYoungDetailsSidebar = ref(false)
    let shallShowYoungCommentsSidebar = ref(false)

    const fetchComment = id => {
      store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/fetchYoungComments`, id)
      shallShowYoungCommentsSidebar = true
    }

    const activeYoung = computed(() => store.getters[`${YOUNGS_STORE_MODULE_NAME}/activeYoung`])
    const messages = computed(() => store.getters[`${YOUNGS_STORE_MODULE_NAME}/messages`])
    const youngsComment = computed(() => store.getters[`${YOUNGS_STORE_MODULE_NAME}/youngsComment`].sort((a, b) => new Date(b.date) - new Date(a.date)))

    const updateYoungComments = id => {
      fetchComment(id)
    }

    const isChatLoading = ref(false) // the property is passed to youngChat

    // True when the young has sent at least one SMS/WhatsApp message (RGPD welcome message was sent)
    const hasYoungMessages = computed(() => messages.value.some(m => m.senderType === 1 && (m.type === 1 || m.type === 2)))

    // SMS sending
    const messageText = ref('')
    const isSending = ref(false)
    const isOpeningConversation = ref(false)
    const currentUser = computed(() => store.getters['users/currentUser'])
    const currentUserRoles = computed(() => currentUser.value?.roles || [])
    const isCoordinateur = computed(() => currentUserRoles.value.includes('LeRefuge.Coordinateur'))
    const isEcoutant = computed(() => currentUserRoles.value.includes('LeRefuge.Ecoutant'))
    const hasSmsPermission = computed(() => {
      const canSendMessage = currentUser.value?.profile?.canSendMessage === true
      return (isCoordinateur.value || isEcoutant.value) && canSendMessage
    })
    const isConversationInProgress = computed(() => activeYoung.value?.conversationStatus === 2)
    const isConversationAssigned = computed(() => Boolean(activeYoung.value?.whoTakesCareUserId))
    const isHandler = computed(() => activeYoung.value?.whoTakesCareUserId === currentUser.value?.id)
    const canOpenConversation = computed(() => hasSmsPermission.value
      && !activeYoung.value?.isBlocked
      && (!isConversationInProgress.value || !isConversationAssigned.value))
    const canSendSms = computed(() => hasSmsPermission.value && isConversationInProgress.value && isHandler.value)

    const sendSms = async () => {
      const body = messageText.value.trim()
      if (!body || isSending.value) { return }
      isSending.value = true
      try {
        await store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/sendMessage`, {
          youngId: activeYoung.value.id,
          body,
        })
        messageText.value = ''
      } catch {
        displayToast('Échec de l\'envoi du message SMS', 'XCircleIcon', 'danger')
      } finally {
        isSending.value = false
      }
    }

    const openChat = id => {
      isChatLoading.value = true
      mqShallShowLeftSidebar.value = false
      fetchComment(id)
      return store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/setActiveYoungId`, id).finally(() => {
        isChatLoading.value = false
      })
    }

    // Search and pagination handlers
    const handleSearch = async params => {
      isYoungListLoading.value = true
      try {
        await store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/fetchYoungs`, params)
      } finally {
        isYoungListLoading.value = false
      }
    }

    const handleLoadMore = async () => {
      if (!paginationInfo.value.hasNext || isYoungListLoading.value) {
        return
      }
      isYoungListLoading.value = true
      try {
        await store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/fetchYoungs`, {
          page: paginationInfo.value.currentPage + 1,
          pageSize: paginationInfo.value.pageSize,
          searchQuery: searchParams.value.searchInput,
          isFilteringByAbsencesCalls: searchParams.value.filterByMissedCalls,
          isFilteringByPendingConversation: searchParams.value.filterByPendingConversation,
        })
      } catch (error) {
        displayToast('Erreur lors du chargement de la suite.', 'XCircleIcon', 'danger')
      } finally {
        isYoungListLoading.value = false
      }
    }

    const changeConversationStatus = id => {
      store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/changeConversationStatus`, id)
        .then(() => {
          displayToast('Conversation mise en attente', 'CheckIcon', 'success')
        })
        .catch((error => {
          displayToast(error.toString(), 'XCircleIcon', 'danger')
        }))
    }

    const openConversationHandler = async () => {
      if (isOpeningConversation.value) { return }
      isOpeningConversation.value = true
      try {
        await store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/openConversation`, activeYoung.value.id)
        displayToast('Conversation prise en charge', 'CheckIcon', 'success')
      } catch {
        displayToast('Échec de la prise en charge', 'XCircleIcon', 'danger')
      } finally {
        isOpeningConversation.value = false
      }
    }

    const closeConversationHandler = async () => {
      try {
        await store.dispatch(`${YOUNGS_STORE_MODULE_NAME}/closeConversation`, activeYoung.value.id)
        displayToast('Conversation close', 'CheckIcon', 'success')
      } catch {
        displayToast('Échec de la clôture', 'XCircleIcon', 'danger')
      }
    }

    onMounted(() => {
      store.dispatch(`${INITIAL_DATA_STORE_MODULE_NAME}/fetchInitialData`)
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Erreur de configuration',
              text: 'Impossible de charger les données de configuration initiales.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })

      // Handle redirection from other pages
      // `youngId` a déjà été extrait globalement dans setup
      if (youngId) {
        const id = Number.parseInt(youngId, 10)
        openChat(id).then(() => {
          if (activeYoung.value && activeYoung.value.id) {
            store.commit(`${YOUNGS_STORE_MODULE_NAME}/updateYoungs`, [activeYoung.value])
          }
        })
      }
    })

    return {
      isYoungListLoading,
      messages,
      youngs,
      youngsComment,
      updateYoungComments,

      paginationInfo,
      handleSearch,
      handleLoadMore,

      // Chat
      isChatLoading,
      activeYoung,
      openChat,
      changeConversationStatus,
      fetchComment,
      hasYoungMessages,

      // SMS
      messageText,
      isSending,
      isCoordinateur,
      canSendSms,
      hasSmsPermission,
      isConversationInProgress,
      isHandler,
      canOpenConversation,
      isOpeningConversation,
      openConversationHandler,
      closeConversationHandler,
      sendSms,

      // UI
      formatPhoneNumber,
      toast,
      displayToast,
      mqShallShowLeftSidebar,
      isWindowSmall,
      openLeftSidebar,
      perfectScrollbarSettings,
      shallShowYoungDetailsSidebar,
      shallShowYoungCommentsSidebar,
    }
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/views/youngs.scss";
</style>
